import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CheckCircleIcon } from 'lucide-react';
// --- Data ---
const PRODUCTS = [{
  name: 'Cinnamon Rolls XL — Edición Coleccionable',
  price: '$6.39',
  img: 'https://cdn.magicpatterns.com/uploads/m4LWU9UjfJ287yR4wVx6ii/Diseno_sin_titulo_(2).png',
  weight: 0.6
}, {
  name: 'Salsas & Cremas Gourmet',
  price: '$7.00',
  img: 'https://cdn.magicpatterns.com/uploads/vS6XcefejdhJHHxjkBVf6h/Salsas_y_cremas_para_postres.png',
  weight: 0.2
}, {
  name: 'Menú Rotativo Rolls XL',
  price: '$9.00',
  img: 'https://cdn.magicpatterns.com/uploads/gGajHXu6zYNuJN1Lh4rrHW/Menu_de_cinnamon_rolls_XL.png',
  weight: 0.2
}];
const NAMES = ['María', 'Carolina', 'Valentina', 'Andrea', 'Sofía', 'Isabella', 'Camila', 'Daniela', 'Gabriela', 'Lucía', 'Ana', 'Paula', 'Natalia', 'Fernanda', 'Alejandra', 'Valeria', 'Mariana', 'Juliana', 'Victoria', 'Martina', 'Emilia', 'Catalina', 'Renata', 'Antonia', 'Florencia'];
const CITIES = ['Bogotá', 'CDMX', 'Lima', 'Buenos Aires', 'Santiago', 'Medellín', 'Guadalajara', 'Quito', 'Monterrey', 'Caracas', 'San José', 'Panamá', 'Santo Domingo', 'Guayaquil', 'Córdoba', 'Rosario', 'Cali', 'Puebla', 'Montevideo', 'Asunción',
// US cities
'Miami', 'Houston', 'Los Angeles', 'New York', 'Chicago', 'Dallas', 'San Antonio', 'Phoenix', 'Orlando', 'Atlanta', 'Denver', 'San Diego', 'Las Vegas', 'Charlotte', 'Tampa', 'Austin', 'Nashville', 'San Francisco', 'Seattle', 'Portland'];
// --- Helpers ---
function getRandomProduct() {
  const rand = Math.random();
  let cumulative = 0;
  for (const product of PRODUCTS) {
    cumulative += product.weight;
    if (rand <= cumulative) return product;
  }
  return PRODUCTS[0];
}
function getRandomItem<T>(array: T[]): T {
  return array[Math.floor(Math.random() * array.length)];
}
function getRandomTime() {
  return Math.floor(Math.random() * 15) + 1; // 1 to 15 mins
}
// --- Component ---
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: (typeof PRODUCTS)[0];
  timeAgo: number;
}
export function RecentPurchaseToast() {
  const [currentToast, setCurrentToast] = useState<ToastData | null>(null);
  const [usedNames, setUsedNames] = useState<string[]>([]);
  const generateToast = useCallback(() => {
    // Select name avoiding recent ones if possible
    let availableNames = NAMES.filter(n => !usedNames.includes(n));
    if (availableNames.length === 0) {
      availableNames = NAMES; // fallback if all used
    }
    const name = getRandomItem(availableNames);
    // Update used names history (keep last 10)
    setUsedNames(prev => {
      const next = [...prev, name];
      if (next.length > 10) next.shift();
      return next;
    });
    const newToast: ToastData = {
      id: Date.now(),
      name,
      city: getRandomItem(CITIES),
      product: getRandomProduct(),
      timeAgo: getRandomTime()
    };
    setCurrentToast(newToast);
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setCurrentToast(null);
    }, 4000);
  }, [usedNames]);
  useEffect(() => {
    // First toast after 3 seconds
    const initialTimer = setTimeout(() => {
      generateToast();
      // Then start the recurring interval (8-15s)
      const scheduleNext = () => {
        const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
        setTimeout(() => {
          generateToast();
          scheduleNext();
        }, nextInterval);
      };
      scheduleNext();
    }, 3000);
    return () => clearTimeout(initialTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <AnimatePresence data-id="element-342">
      {currentToast && <motion.div key={currentToast.id} initial={{
      opacity: 0,
      y: 50
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.4,
      ease: 'easeOut'
    }} className="fixed z-40 md:left-4 md:bottom-6 bottom-[80px] left-3 right-3 md:right-auto md:w-[320px] bg-white rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-gray-100 p-3 overflow-hidden" data-id="element-343">
          <button onClick={() => setCurrentToast(null)} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Cerrar notificación" data-id="element-344">
            <XIcon className="w-4 h-4" data-id="element-345" />
          </button>

          <div className="flex items-center gap-3 pr-4" data-id="element-346">
            <div className="w-12 h-12 flex-shrink-0 rounded-xl overflow-hidden bg-gray-50 border border-gray-100" data-id="element-347">
              <img src={currentToast.product.img} alt={currentToast.product.name} className="w-full h-full object-cover" data-id="element-348" />
            </div>

            <div className="flex-1 min-w-0" data-id="element-349">
              <div className="flex items-center gap-1 mb-0.5" data-id="element-350">
                <p className="text-[13px] text-gray-600 truncate" data-id="element-351">
                  <span className="font-semibold text-gray-900" data-id="element-352">
                    {currentToast.name}
                  </span>{' '}
                  en {currentToast.city} acaba de comprar
                </p>
              </div>

              <p className="text-[14px] font-bold text-[#1A1614] leading-tight mb-1 truncate" data-id="element-353">
                {currentToast.product.name} —{' '}
                <span className="text-[#e76f51]" data-id="element-354">
                  {currentToast.product.price}
                </span>
              </p>

              <div className="flex items-center gap-1" data-id="element-355">
                <CheckCircleIcon className="w-3.5 h-3.5 text-[#07c86e]" data-id="element-356" />
                <span className="text-[11px] font-medium text-gray-500" data-id="element-357">
                  Verificado · hace {currentToast.timeAgo} min
                </span>
              </div>
            </div>
          </div>
        </motion.div>}
    </AnimatePresence>;
}